import { Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ROLES } from "@/help/roles";
import { ARTICLES } from "@/help/articles";

/**
 * Wraps an article body. Shows the title, summary, "available to"
 * badges (which roles this article applies to), then the body, then
 * a "Related" footer with prev/next from the active role's curated
 * order plus cross-role links if any.
 *
 * Article components themselves render only the body — this wrapper
 * provides the header/footer chrome so all articles look consistent.
 *
 * Props:
 *   - article: an entry from ARTICLES
 *   - role:    the role context the user came from (optional)
 *   - children: article body (rendered between header and footer)
 */
export default function Article({ article, role, children }) {
  return (
    <article className="space-y-6 text-foreground">
      <header className="space-y-4 pb-6 border-b border-border">
        <h1 className="font-display text-3xl md:text-4xl font-semibold leading-tight">
          {article.title}
        </h1>
        {article.summary && (
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {article.summary}
          </p>
        )}
        <AvailableToBadges roles={article.roles} activeRole={role?.slug} />
      </header>

      <div className="prose-help space-y-4 max-w-2xl">{children}</div>

      <ArticleFooter article={article} role={role} />
    </article>
  );
}

function AvailableToBadges({ roles, activeRole }) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mr-1">
        For
      </span>
      {roles.map((r) => {
        const role = ROLES[r];
        if (!role) return null;
        const isActive = r === activeRole;
        return (
          <Link
            key={r}
            href={role.helpPath}
            className={`inline-flex items-center rounded-full text-[11px] uppercase tracking-wider px-2.5 py-0.5 transition-colors ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {role.short}
          </Link>
        );
      })}
    </div>
  );
}

function ArticleFooter({ article, role }) {
  if (!role) return null;

  const slugs = role.sections.flatMap((s) => s.slugs);
  const idx = slugs.indexOf(article.slug);
  const prev = idx > 0 ? ARTICLES[slugs[idx - 1]] : null;
  const next = idx >= 0 && idx < slugs.length - 1 ? ARTICLES[slugs[idx + 1]] : null;

  return (
    <footer className="pt-8 border-t border-border space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        {prev ? (
          <NavCard direction="prev" article={prev} role={role} />
        ) : (
          <div />
        )}
        {next ? <NavCard direction="next" article={next} role={role} /> : <div />}
      </div>
    </footer>
  );
}

function NavCard({ direction, article, role }) {
  const isNext = direction === "next";
  return (
    <Link
      href={`/help/articles/${article.slug}?from=${role.slug}`}
      className={`block rounded-xl border border-border bg-card hover:bg-secondary/40 transition-colors p-4 ${
        isNext ? "text-right" : ""
      }`}
    >
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {isNext ? "Next" : "Previous"}
      </p>
      <p className="font-display font-semibold text-foreground leading-snug">
        {isNext ? (
          <>
            {article.title} <ArrowRight className="inline h-4 w-4 -mt-0.5 ml-0.5" />
          </>
        ) : (
          article.title
        )}
      </p>
    </Link>
  );
}
